/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { getProductUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { Button, Heading, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { CartLineItemComponent } from '@msdyn365-commerce/components';
import { getUrlSync } from '@msdyn365-commerce/core';
import {
    CartLine,
    OrgUnitLocation,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classnames from 'classnames';
import get from 'lodash/get';
import * as React from 'react';
import { IFarmlandsCheckoutData } from '../farmlands-checkout.data';
import { IFarmlandsCheckoutProps } from '../farmlands-checkout.props.autogenerated';

type deliveryType = 'pickup' | 'ship';

export interface IPickUpAtStore {
    PickUpAtStore: INodeProps;
    label: React.ReactNode;
    location: React.ReactNode;
}

export interface ILineItem {
    LineId: string;
    LineItem: INodeProps;
    item?: React.ReactNode;
    pickUpAtStore?: IPickUpAtStore;
}

export interface ILineItemDeliveryGroup {
    LineItemDeliveryGroup: INodeProps;
    LineItemList: INodeProps;
    heading: React.ReactNode;
    lineItems: ILineItem[];
}

export interface ILineItems {
    LineItems: INodeProps;
    Header: INodeProps;
    heading: React.ReactNode;
    editLink: React.ReactNode;
    itemsForPickup?: ILineItemDeliveryGroup;
    itemsForShip?: ILineItemDeliveryGroup;
}

// tslint:disable-next-line:max-func-body-length
export const getLineItems = (input: IFarmlandsCheckoutProps<IFarmlandsCheckoutData>): ILineItems | undefined => {
    const _getDeliveryLocation = (cartLine: CartLine): OrgUnitLocation | undefined => {
        const orgUnitLocations = get(input, 'data.orgUnitLocations.result');
        if (!cartLine.FulfillmentStoreId || !orgUnitLocations) {
            return;
        }

        const locationMatch = (orgUnitLocations || []).find((location: OrgUnitLocation): boolean => {
            return location.OrgUnitNumber === cartLine.FulfillmentStoreId;
        });

        return locationMatch && locationMatch;
    };

    const _getProduct = (productId: number): SimpleProduct | undefined => {
        const products = get(input, 'data.products.result') || [];
        return products.find((_product: SimpleProduct) => {
            return productId === _product.RecordId;
        });
    };

    const _getLineItemComponent = (cartLine: CartLine): JSX.Element | undefined => {
        const product = cartLine.ProductId && _getProduct(cartLine.ProductId);

        const {
            id,
            typeName,
            context,
            config: { imageSettings },
            resources: {
                quantityDisplayString,
                productDimensionTypeColor,
                productDimensionTypeSize,
                productDimensionTypeStyle,
                configString,
                inputQuantityAriaLabel,
                discountStringText
            },
            context: {
                request: { gridSettings }
            }
        } = input;

        if (!product) {
            return;
        }

        return (
            <>
                <CartLineItemComponent
                    id={id}
                    typeName={typeName}
                    context={context}
                    resources={{
                        sizeString: productDimensionTypeSize,
                        colorString: productDimensionTypeColor,
                        styleString: productDimensionTypeStyle,
                        quantityDisplayString,
                        configString,
                        inputQuantityAriaLabel,
                        discountStringText,
                        originalPriceText: '',
                        currentPriceText: '',
                        shippingChargesText: ''
                    }}
                    isQuantityEditable={false}
                    imageSettings={imageSettings!}
                    gridSettings={gridSettings!}
                    productUrl={getProductUrlSync(product, context.actionContext, undefined)}
                    primaryImageUrl={product.PrimaryImageUrl}
                    data={{
                        cartLine: cartLine,
                        product: product
                    }}
                    displayMode='COMPACT'
                />
                <input className='product-id' type='hidden' value={product.RecordId} />
            </>
        );
    };

    const _getPickUpAtStoreComponents = (pickupDeliveryLocation?: OrgUnitLocation): IPickUpAtStore => {
        const {
            resources: { pickUpAtStoreWithLocationText }
        } = input;

        return {
            PickUpAtStore: { className: 'ms-checkout__pick-up-at-store' },
            label: <span className='ms-checkout__store-label'>{pickUpAtStoreWithLocationText}</span>,
            location: (
                <>
                    <a className='ms-checkout__change-store-location btn' href={'/cart'}>
                        Change store
                    </a>
                    <div className='ms-checkout__store-location'>
                        Farmlands {pickupDeliveryLocation?.OrgUnitName}
                        <br />
                        {pickupDeliveryLocation?.Address}
                    </div>
                </>
            )
        };
    };

    const _getLineItemsComponents = (items: CartLine[]): ILineItem[] => {
        const pickupDeliveryModeCode = get(input, 'context.request.channel.PickupDeliveryModeCode');
        const cart = get(input, 'data.checkout.result.checkoutCart.cart');
        return items.map((cartLine, index) => {
            const pickupDeliveryLocation = _getDeliveryLocation(cartLine);
            const isPickUp = cart.DeliveryMode === pickupDeliveryModeCode;

            return {
                LineId: cartLine.LineId || '',
                LineItem: { className: 'ms-checkout__line-item' },
                item: _getLineItemComponent(cartLine),
                pickUpAtStore:
                    isPickUp && index === items.length - 1
                        ? _getPickUpAtStoreComponents(pickupDeliveryLocation)
                        : undefined
            };
        });
    };

    const _filterItemsByDiliveryType = (type: deliveryType): CartLine[] => {
        const cart = get(input, 'data.checkout.result.checkoutCart.cart');
        const pickupDeliveryModeCode = get(input, 'context.request.channel.PickupDeliveryModeCode');
        return type === 'ship'
            ? cart.CartLines.filter((cartLine: CartLine) => cartLine.DeliveryMode !== pickupDeliveryModeCode)
            : cart.CartLines.filter((cartLine: CartLine) => cartLine.DeliveryMode === pickupDeliveryModeCode);
    };

    const _getLineItemsByDeliveryType = (type: deliveryType): ILineItemDeliveryGroup | undefined => {
        const items = _filterItemsByDiliveryType(type);

        if (items.length === 0) {
            return undefined;
        }

        return {
            LineItemDeliveryGroup: { className: classnames('ms-checkout__line-items-delivery-group', type) },
            LineItemList: { className: 'ms-checkout__line-items-list' },
            heading: <div className='ms-checkout__line-items-group-title'>{ }</div>,
            lineItems: _getLineItemsComponents(items)
        };
    };

    const _getLineItems = (): ILineItems | undefined => {
        const {
            context,
            config: { lineItemsHeading },
            data: { products },
            resources: { editCartText }
        } = input;

        const cart = get(input, 'data.checkout.result.checkoutCart.cart');

        if (!cart || !cart.CartLines || !cart.CartLines.length || !products) {
            return undefined;
        }

        return {
            LineItems: { className: 'ms-checkout__line-items' },
            Header: { className: 'ms-checkout__line-items-header' },
            heading: lineItemsHeading && <Heading className='ms-checkout__line-items-heading' {...lineItemsHeading} />,
            editLink: (
                <Button
                    className='ms-checkout__line-items-edit-cart-link'
                    title={editCartText}
                    color='link'
                    href={getUrlSync('cart', context.actionContext) || ''}
                >
                    {editCartText}
                </Button>
            ),
            itemsForPickup: _getLineItemsByDeliveryType('pickup'),
            itemsForShip: _getLineItemsByDeliveryType('ship')
        };
    };
    return _getLineItems();
};
